import Constants from '@/Constants';
import InputSearch from '@/components/InputSearch';
import Loader from '@/components/Loader';
import ScrolledCard from '@/components/ScrolledCard';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import { ProgramClass } from '@/models/ProgramClass';
import { ApplicationState } from '@/store';
import { enrollStudentRequest, unenrollStudentRequest } from '@/store/programClass/actions';
import { ClassEnrollment, PersonWithEnrollment } from '@/store/programClass/types';
import React, { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import CardStudent from './StudentCard';
import './style.scss';

interface Props {
    onlyEnrolled?: boolean;
    show: boolean;
    setShow: CallableFunction;
    programClassId: number;
    enrollmentsCount: number;
    setTotalEnrollments: Dispatch<SetStateAction<number>>;
}

const FindPersonModal = (props: Props) => {
    const { show, setShow, onlyEnrolled, programClassId, setTotalEnrollments, enrollmentsCount } = props;
    const dispatch = useDispatch();

    const { strings, newEnrollment } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        newEnrollment: state.programClass.enrollment,
    }));

    const [loadingScroll, setLoadingScroll] = useState<boolean>(false);
    const [searchName, setSearchName] = useState<string>('');

    const swrResultKeyUri = useRef<string>('');

    const getKey = (index: number) => {
        const limit = 10;
        const offset = index * limit;
        let URI: string = '';

        if (onlyEnrolled) {
            URI = `${Constants.LMS_CLASS_ENTOLLMENTS_URI}?programClassId=${programClassId}&getPersonData=true`;
        } else {
            URI = `${Constants.LMS_PROGRAM_CLASS_ENROLL_INFO_URI.replace(
                '%p',
                programClassId.toString(),
            )}?onlyActive=true&profileKey=STUDENT&orderBy=person.firstName,person.lastName`;
        }

        if (searchName) {
            URI += `&search=${searchName}`;
        }

        URI += `&offset=${offset}&limit=${limit}`;

        swrResultKeyUri.current = URI;

        return show ? URI : null;
    };

    const { data: studentList, originalData, loading, total, clear, mutate, loadMore } = useGetPaginated<ClassEnrollment | PersonWithEnrollment>(
        getKey,
        {
            revalidateOnMount: true,
        },
    );

    useEffect(() => {
        setSearchName('');
        if (!show) {
            clear(swrResultKeyUri.current);
        }
    }, [show]);

    useEffect(() => {
        if (newEnrollment?.id) {
            refreshLocalList(true, newEnrollment.personId, newEnrollment.id);
        }
    }, [newEnrollment]);

    const getEnrollStatus = (data: PersonWithEnrollment | ClassEnrollment): boolean => {
        return !!((data as PersonWithEnrollment).enrolled || (data as ClassEnrollment).id);
    };

    const toggleEnroll = (data: PersonWithEnrollment | ClassEnrollment, doEnroll: boolean) => {
        if (doEnroll) {
            dispatch(
                enrollStudentRequest({
                    personId: data.person.id as number,
                    programClass: { id: programClassId } as ProgramClass,
                } as ClassEnrollment),
            );
            setTotalEnrollments((oldState: number) => oldState + 1);
        } else {
            dispatch(unenrollStudentRequest((data as ClassEnrollment).id || (data as PersonWithEnrollment).classEnrollmentId));
            setTotalEnrollments((oldState: number) => (oldState > 0 ? oldState - 1 : 0));
            refreshLocalList(false, Number(data.person.id), null);
        }
    };

    const refreshLocalList = (enrollStatus: boolean, personId: number, classEnrollmentId: number | null) => {
        const localList = originalData ? [...originalData] : [];
        const updatedItem: PersonWithEnrollment | ClassEnrollment | undefined = _.chain(localList)
            .pluck('result')
            .flatten()
            .find((x: PersonWithEnrollment | ClassEnrollment) => x.person?.id === personId)
            .value();

        if (updatedItem) {
            (updatedItem as PersonWithEnrollment).enrolled = enrollStatus;
            (updatedItem as PersonWithEnrollment).classEnrollmentId = Number(classEnrollmentId);
            (updatedItem as ClassEnrollment).id = Number(classEnrollmentId);
            mutate(localList, false);
        }
    };

    const callbackScrollListener = React.useCallback(() => {
        if (show && !loading && studentList.length < total) {
            setLoadingScroll(true);
            loadMore();
        } else if (!loading) setLoadingScroll(false);
    }, [studentList, searchName, loading]);

    const scrollRef = useBottomScrollListener(callbackScrollListener, { offset: 200 }) as RefObject<HTMLDivElement>;

    const modalTitle = { __html: strings.ALL_N_STUDENTS_ENROLLED.replace('%n', `<span>${enrollmentsCount}</span>`) };

    return (
        <Modal aria-labelledby="contained-modal-title-vcenter" centered show={show} size="lg">
            <Modal.Body>
                <div className="find-person-modal">
                    <i className="material-icons-outlined close-icon" onClick={() => setShow(false)}>
                        {Constants.icons.CLOSE}
                    </i>

                    <div>
                        {onlyEnrolled ? <label dangerouslySetInnerHTML={modalTitle}></label> : <p>{strings.ENROLL_STUDENTS}</p>}

                        <InputSearch placeholder={strings.SEARCH_FOR_STUDENT} setValue={setSearchName} value={searchName} />
                    </div>

                    <ScrolledCard divRef={scrollRef} list={studentList}>
                        {loading && !loadingScroll ? (
                            <Loader />
                        ) : (
                            (studentList as PersonWithEnrollment[]).map((data: PersonWithEnrollment) => {
                                return (
                                    <CardStudent
                                        automaticEnroll={data.automaticEnroll}
                                        enrolled={getEnrollStatus(data)}
                                        enrolledInAnotherClass={data.enrolledInAnotherClass}
                                        handleChange={(doEnroll: boolean) => {
                                            toggleEnroll(data, doEnroll);
                                        }}
                                        key={data.person.id}
                                        name={`${data.person.firstName} ${data.person.lastName}`}
                                        thumbnail={data.person.thumbnail}
                                    />
                                );
                            })
                        )}
                        {loadingScroll && <Loader />}
                    </ScrolledCard>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default FindPersonModal;
